import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSimpleWebSocket } from '../../hooks/useSimpleWebSocket';
import { 
  Send, MessageSquare, Star, Flag
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  getMessages, sendMessage, markMessagesAsRead,
  getActiveConversationEscrow, confirmEscrowReceipt,
  getConversationEscrowHistory, raiseEscrowDispute, submitRating,
  reportSeller // NEW: Import the reportSeller API function
} from '../../services/api';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';

// Import components
import ChatHeader from './ChatHeader';
import EscrowModal from './EscrowModal';
import EscrowActions from './EscrowActions';

// Seller Report Reasons - similar to listing reports but for sellers
const SELLER_REPORT_REASONS = [
  { id: 'fake_store', label: 'Fake Store', description: 'Store appears to be fake or non-existent' },
  { id: 'scam', label: 'Scam or Fraud', description: 'Suspected fraudulent activity or scam' },
  { id: 'harassment', label: 'Harassment', description: 'Offensive behavior or harassment' },
  { id: 'fake_reviews', label: 'Fake Reviews', description: 'Creating fake reviews for their store' },
  { id: 'prohibited_items', label: 'Prohibited Items', description: 'Selling prohibited or illegal items' },
  { id: 'misleading_info', label: 'Misleading Information', description: 'False or misleading store information' },
  { id: 'poor_service', label: 'Poor Service', description: 'Very poor customer service' },
  { id: 'not_responding', label: 'Not Responding', description: 'Seller is not responding to messages' },
  { id: 'spam_messages', label: 'Spam Messages', description: 'Sending spam or unwanted messages' },
  { id: 'other', label: 'Other Reason', description: 'Another issue not listed above' },
];

const ChatDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Get data from navigation state
  const { 
    sellerId, 
    sellerName, 
    sellerProfilePicture,
    sellerStoreLogo,
    sellerStoreName,
    buyerId,
    buyerName,
    buyerProfilePicture,
    listingTitle, 
    listingPrice,
    listingImage,
    listingId,
  } = location.state || {};
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Escrow state
  const [activeEscrow, setActiveEscrow] = useState(null);
  const [escrowHistory, setEscrowHistory] = useState([]);
  const [processingAction, setProcessingAction] = useState(false);
  
  // Modal states
  const [showEscrowModal, setShowEscrowModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Rate/Report states - SEPARATED
  const [showRateModal, setShowRateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  
  // Rating state (separate from reporting)
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  
  // Reporting state (separate from rating)
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  
  const messagesEndRef = useRef(null);
  const conversationId = parseInt(id, 10);
  const { isConnected, subscribeToConversation } = useSimpleWebSocket();
  const [lastMessageTime, setLastMessageTime] = useState(null);

  // Determine if current user is buyer or seller
  const isCurrentUserBuyer = user?.user_id === buyerId;
  const isCurrentUserSeller = user?.user_id === sellerId;

  // Initialize chat and escrow
  useEffect(() => {
    if (conversationId && user) {
      initializeChat();
      loadEscrowData();
    }
  }, [conversationId, user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeChat = async () => {
    try {
      setLoading(true);
      const response = await getMessages(conversationId);
      
      // Ensure all message IDs are numbers for consistency
      const formattedMessages = (response.data || []).map(msg => ({
        ...msg,
        message_id: parseInt(msg.message_id) || msg.message_id
      }));
      
      setMessages(formattedMessages);
      
      // Set last message time for offline sync
      if (formattedMessages.length > 0) {
        const latestMessage = formattedMessages[formattedMessages.length - 1];
        setLastMessageTime(latestMessage.created_at);
      }
      
      if (user?.user_id) {
        await markMessagesAsRead(conversationId, user.user_id);
      }
    } catch (error) {
      // console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncOfflineMessages = async () => {
    try {
      // console.log('🔄 Syncing offline messages...');
      
      // Fetch messages from server
      const response = await getMessages(conversationId);
      const serverMessages = (response.data || []).map(msg => ({
        ...msg,
        message_id: parseInt(msg.message_id) || msg.message_id
      }));
      
      setMessages(prevMessages => {
        // Create a map of existing message IDs for quick lookup
        const existingIds = new Set(prevMessages.map(msg => msg.message_id));
        
        // Filter out messages that already exist in our state
        const newMessages = serverMessages.filter(
          serverMsg => !existingIds.has(serverMsg.message_id)
        );
        
        if (newMessages.length > 0) {
          // console.log(`✅ Found ${newMessages.length} new messages while offline`);
          
          // Update last message time
          const latestNewMessage = newMessages[newMessages.length - 1];
          setLastMessageTime(latestNewMessage.created_at);
          
          // Mark new messages as read
          if (user?.user_id) {
            markMessagesAsRead(conversationId, user.user_id);
          }
        }
        
        return [...prevMessages, ...newMessages];
      });
      
    } catch (error) {
      // console.error('Error syncing offline messages:', error);
    }
  };

  useEffect(() => {
    
    // Sync messages when coming back online
    syncOfflineMessages();
    
    const unsubscribe = subscribeToConversation(conversationId, (newMessage) => {
      // console.log('📨 WebSocket message received:', newMessage);
      
      // NORMALIZE THE MESSAGE
      const normalizedMessage = {
        ...newMessage,
        message_id: parseInt(newMessage.message_id) || newMessage.message_id,
        // Ensure created_at is in proper format
        created_at: newMessage.created_at || new Date().toISOString()
      };
      
      // console.log('📨 Processed message:', normalizedMessage);
      
      setMessages(prev => {
        // Check if message already exists (compare by ID)
        const messageExists = prev.some(msg => 
          msg.message_id === normalizedMessage.message_id
        );
        
        if (messageExists) {
          // console.log('⚠️ Duplicate message, ignoring:', normalizedMessage.message_id);
          return prev;
        }
        
        // console.log('✅ Adding new message to state');
        
        // Update last message time
        setLastMessageTime(normalizedMessage.created_at);
        
        return [...prev, normalizedMessage];
      });
      
      // Mark as read if from other user
      if (normalizedMessage.sender_id !== user.user_id) {
        // console.log('👁️ Marking message as read');
        markMessagesAsRead(conversationId, user.user_id);
      }
    });
    
    return () => {
      // console.log(`🧹 Cleaning up subscription for conversation ${conversationId}`);
      unsubscribe();
    };
  }, [isConnected, conversationId, user?.user_id, subscribeToConversation]);
  

  const loadEscrowData = async () => {
    try {
      const escrowResponse = await getActiveConversationEscrow(conversationId);
      if (escrowResponse.success && escrowResponse.data) {
        setActiveEscrow(escrowResponse.data);
      }
      
      const historyResponse = await getConversationEscrowHistory(conversationId);
      if (historyResponse.success) {
        setEscrowHistory(historyResponse.data || []);
      }
    } catch (error) {
      // console.error('Error loading escrow data:', error);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Handle MySQL datetime format: "2026-01-18 18:59:39"
      const mysqlFormat = dateString.replace(' ', 'T') + 'Z';
      const date = new Date(mysqlFormat);
      
      // Fallback for invalid dates
      if (isNaN(date.getTime())) {
        // Try parsing as ISO string
        const fallbackDate = new Date(dateString);
        if (!isNaN(fallbackDate.getTime())) {
          return fallbackDate.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
        }
        return '';
      }
      
      return date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (error) {
      // console.error('Date formatting error:', error, 'for date:', dateString);
      return '';
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;

    setSending(true);
    const tempId = Date.now();
    const tempMessage = {
      message_id: tempId,
      sender_id: user.user_id,
      message_text: input.trim(),
      created_at: new Date().toISOString(),
      isTemp: true,
    };

    setMessages(prev => [...prev, tempMessage]);
    setInput('');

    try {
      const response = await sendMessage({
        conversation_id: conversationId,
        sender_id: user.user_id,
        message_text: input.trim(),
      });

      setMessages(prev => prev.map(msg => 
        msg.isTemp && msg.message_id === tempId 
          ? { ...response.data, isTemp: false } 
          : msg
      ));
    } catch (error) {
      // console.error('Error sending message:', error);
      setMessages(prev => prev.filter(msg => !msg.isTemp || msg.message_id !== tempId));
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleConfirmReceipt = async () => {
    if (!activeEscrow || !user) return;
    
    const confirmed = window.confirm(
      `Confirm you have received the item in good condition?\n\n` +
      `✅ This will release ${formatPrice(activeEscrow.amount)} to the seller.\n` +
      `Transaction will be completed.`
    );
    
    if (!confirmed) return;
    
    try {
      setProcessingAction(true);
      const result = await confirmEscrowReceipt(activeEscrow.escrow_id, {
        user_id: user.user_id
      });
      
      if (result.success) {
        alert('✅ Payment released to seller!');
        await loadEscrowData();
        setShowEscrowModal(false);
      } else {
        alert('❌ Error: ' + (result.error || 'Failed to confirm receipt'));
      }
    } catch (error) {
      // console.error('Confirm receipt error:', error);
      alert('❌ Error: Failed to confirm receipt');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRaiseDispute = () => {
    if (!activeEscrow) return;
    
    navigate(`/disputes/raise`, {
      state: {
        escrowId: activeEscrow.escrow_id,
        conversationId: conversationId,
        amount: activeEscrow.amount,
        sellerId: sellerId,
        buyerId: buyerId,
        listingTitle: listingTitle || 'Item',
        fromChat: true,
        chatState: location.state
      }
    });
  };

  const handlePayIntoEscrow = () => {
    const finalSellerId = sellerId;
    
    if (!finalSellerId) {
      alert('Cannot determine seller. Please refresh the page.');
      return;
    }
    
    navigate(`/chats/${conversationId}/escrow-payment`, {
      state: {
        listingTitle: listingTitle || 'Item',
        sellerName: sellerStoreName || sellerName,
        sellerId: finalSellerId,
        conversationId: conversationId
      }
    });
  };

  const handleManualRefresh = async () => {
    await loadEscrowData();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Updated handleSubmitRating function
  const handleSubmitRating = async () => {
    // Validate rating
    if (rating === 0) {
      alert('Please select a rating (1-5 stars)');
      return;
    }
  
    try {
      setSubmittingRating(true);
      
      // Try to find order_id from escrow
      let orderId = null;
      
      // Check active escrow
      if (activeEscrow?.order_id) {
        orderId = activeEscrow.order_id;
      }
      
      // Check escrow history
      if (!orderId && escrowHistory.length > 0) {
        const escrowWithOrder = escrowHistory.find(h => h.order_id);
        if (escrowWithOrder) {
          orderId = escrowWithOrder.order_id;
        }
      }
      
      // Get listing_id
      let listingId = location.state?.listingId || activeEscrow?.listing_id || null;
      
      // Prepare rating data
      const ratingData = {
        buyer_id: user?.user_id,
        seller_id: sellerId, // Include seller_id for chat ratings
        conversation_id: conversationId,
        escrow_id: activeEscrow?.escrow_id || null,
        listing_id: listingId,
        rating_overall: rating,
        review_text: reviewText.trim() || null,
        rating_quality: null,
        rating_communication: null,
        rating_delivery: null,
      };
      
      // If no orderId found, set it to 0 for chat-based ratings
      const finalOrderId = orderId || 0;
         
      // Call the API
      const result = await submitRating(finalOrderId, ratingData);
      
      if (result.success) {
        alert('✅ Rating submitted successfully! It will be visible after approval.');
        setShowRateModal(false);
        setRating(0);
        setReviewText('');
      } else {
        throw new Error(result.error || 'Failed to submit rating');
      }
    } catch (error) {
      // console.error('Error submitting rating:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setSubmittingRating(false);
    }
  };

  // NEW: Proper seller reporting function
  const handleReportSeller = async () => {
    // Use the user_id as seller_id (since sellers table uses user_id)
    const sellerUserId = sellerId; // This is already the user_id from your navigateToChat
    
    if (!sellerUserId) {
      // console.error('Seller user ID not found');
      alert('Unable to identify seller. Please try again.');
      return;
    }
    
    // Validate report reason
    if (!reportReason) {
      alert('Please select a reason for reporting');
      return;
    }
    
    // If "other" reason selected, require description
    if (reportReason === 'other' && !reportDescription.trim()) {
      alert('Please provide details for your report');
      return;
    }
    
    if (!user?.user_id) {
      alert('Please log in to report a seller');
      return;
    }
    
    try {
      setSubmittingReport(true);
      
      // Prepare report data - using user_id as seller_id
      const reportData = {
        seller_id: sellerUserId, // This is user_id, but that's what your Seller model expects
        reported_by: user.user_id,
        reason: reportReason,
        description: reportReason === 'other' 
          ? reportDescription 
          : SELLER_REPORT_REASONS.find(r => r.id === reportReason)?.description || null
      };
      
      // console.log('Submitting seller report with user_id:', reportData);
      
      // Call the API function
      const result = await reportSeller(reportData);
      
      if (result.success) {
        alert('✅ Seller report submitted successfully! Our team will review it.');
        setShowReportModal(false);
        // Reset form
        setReportReason('');
        setReportDescription('');
      } else {
        alert(`❌ Failed to submit report: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      // console.error('Error reporting seller:', error);
      alert(`❌ Error: ${error.message || 'Failed to submit report'}`);
    } finally {
      setSubmittingReport(false);
    }
  };

  // Helper to get selected reason description
  const getSelectedReasonDescription = () => {
    return SELLER_REPORT_REASONS.find(reason => reason.id === reportReason)?.description || '';
  };

  if (loading && messages.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading chat..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <ChatHeader
        navigate={navigate}
        isCurrentUserBuyer={isCurrentUserBuyer}
        isCurrentUserSeller={isCurrentUserSeller}
        sellerId={sellerId}
        sellerName={sellerName}
        sellerStoreLogo={sellerStoreLogo}
        sellerStoreName={sellerStoreName}
        buyerName={buyerName}
        buyerProfilePicture={buyerProfilePicture}
        listingTitle={listingTitle}
        listingPrice={listingPrice}
        listingImage={listingImage}
        activeEscrow={activeEscrow}
        handleManualRefresh={handleManualRefresh}
        showDropdown={showDropdown}
        setShowDropdown={setShowDropdown}
        setShowRateModal={setShowRateModal}
        setShowReportModal={setShowReportModal}
        setShowEscrowModal={setShowEscrowModal}
      />

      {/* Messages Container */}
      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        <div className="space-y-4">
          {messages.length === 0 && !loading ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No messages yet</h3>
              <p className="text-gray-600">
                Start the conversation by sending a message
              </p>
            </div>
          ) : (
            messages.map((message, index) => {
              const isMyMessage = message.sender_id === user?.user_id;
              const isSystem = message.sender_id === 'system';
              
              if (isSystem) {
                return (
                  <div key={message.message_id || index} className="text-center">
                    <div className="inline-block px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-full">
                      {message.message_text}
                    </div>
                  </div>
                );
              }
              
              return (
                <div key={message.message_id || index} className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                    isMyMessage 
                      ? 'bg-orange-500 text-white rounded-tr-none' 
                      : 'bg-white text-gray-900 border border-gray-200 rounded-tl-none shadow-sm'
                  }`}>
                    <p className="break-words whitespace-pre-wrap">{message.message_text}</p>
                    <div className={`text-xs mt-1 flex justify-end ${
                      isMyMessage ? 'text-orange-200' : 'text-gray-500'
                    }`}>
                      {formatTime(message.created_at)}
                      {message.isTemp && ' ⏳'}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-end gap-3">
            <div className="flex-1 bg-gray-100 rounded-2xl p-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="w-full bg-transparent border-0 focus:outline-none focus:ring-0 resize-none px-3 py-2"
                rows="1"
                style={{ maxHeight: '120px' }}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="bg-orange-500 text-white p-3 rounded-xl hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Escrow Modal */}
      <EscrowModal
        isOpen={showEscrowModal}
        onClose={() => setShowEscrowModal(false)}
        activeEscrow={activeEscrow}
        escrowHistory={escrowHistory}
        isCurrentUserBuyer={isCurrentUserBuyer}
        isCurrentUserSeller={isCurrentUserSeller}
        sellerName={sellerName}
        sellerStoreName={sellerStoreName}
        buyerName={buyerName}
        formatPrice={formatPrice}
        renderEscrowActions={() => (
          <EscrowActions
            activeEscrow={activeEscrow}
            isCurrentUserBuyer={isCurrentUserBuyer}
            isCurrentUserSeller={isCurrentUserSeller}
            processingAction={processingAction}
            handleConfirmReceipt={handleConfirmReceipt}
            handlePayIntoEscrow={handlePayIntoEscrow}
            navigate={navigate}
            conversationId={conversationId}
            listingTitle={listingTitle}
            sellerStoreName={sellerStoreName}
            sellerName={sellerName}
            sellerId={sellerId}
            buyerId={buyerId}
            amount={activeEscrow?.amount}
            locationState={location.state}
          />
        )}
        navigate={navigate}
        conversationId={conversationId}
      />

      {/* Enhanced Rate Seller Modal */}
      <Modal
        isOpen={showRateModal}
        onClose={() => {
          setShowRateModal(false);
          setRating(0);
          setReviewText('');
        }}
        title="Rate Your Experience"
        size="md"
        showCloseButton={true}
        showFooter={true}
        confirmText={submittingRating ? "Submitting..." : "Submit Rating"}
        cancelText="Cancel"
        onConfirm={handleSubmitRating}
        onCancel={() => {
          setShowRateModal(false);
          setRating(0);
          setReviewText('');
        }}
        confirmButtonType="primary"
        disabled={submittingRating}
      >
        <div className="py-4">
          {/* Stars Rating */}
          <div className="text-center mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Overall Rating</p>
            <div className="flex justify-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="text-3xl focus:outline-none transition-transform hover:scale-110"
                  style={{ color: star <= rating ? '#FF6B00' : '#D1D5DB' }}
                >
                  ★
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-600">
              {rating === 0 && 'Select a rating'}
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent'}
            </p>
          </div>

          {/* Review Text Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Review (Optional)
            </label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience with this seller..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              rows="4"
              maxLength="1000"
            />
            <p className="text-xs text-gray-500 mt-1">
              {reviewText.length}/1000 characters
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Note:</span> Your rating will be submitted for approval and will be visible to other buyers once approved.
            </p>
          </div>
        </div>
      </Modal>

      {/* NEW: Enhanced Report Seller Modal with structured reasons */}
      <Modal
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setReportReason('');
          setReportDescription('');
        }}
        title="Report Seller"
        size="lg"
        showCloseButton={true}
        showFooter={true}
        confirmText={submittingReport ? "Submitting..." : "Submit Report"}
        cancelText="Cancel"
        onConfirm={handleReportSeller}
        onCancel={() => {
          setShowReportModal(false);
          setReportReason('');
          setReportDescription('');
        }}
        confirmButtonType="danger"
        disabled={submittingReport || !reportReason || (reportReason === 'other' && !reportDescription.trim())}
      >
        <div className="py-4 max-h-[60vh] overflow-y-auto">
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Why are you reporting this seller?</h4>
            <p className="text-sm text-gray-600 mb-4">
              Select the most appropriate reason. Your report will be reviewed by our moderation team.
            </p>

            <div className="space-y-3">
              {SELLER_REPORT_REASONS.map((reason) => (
                <label
                  key={reason.id}
                  className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    reportReason === reason.id
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center h-5">
                    <input
                      type="radio"
                      name="seller-report-reason"
                      checked={reportReason === reason.id}
                      onChange={() => setReportReason(reason.id)}
                      className="h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500"
                    />
                  </div>
                  <div className="ml-3 flex-1">
                    <span className="font-medium text-gray-900">{reason.label}</span>
                    <p className="text-sm text-gray-600">{reason.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          {reportReason === 'other' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Please provide details
              </label>
              <textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Describe the issue in detail..."
              />
            </div>
          )}

          {/* Selected Reason Info */}
          {reportReason && reportReason !== 'other' && (
            <div className="bg-red-50 rounded-lg p-4 mb-6">
              <div className="flex">
                <Flag className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="ml-3 text-sm text-red-800">
                  {getSelectedReasonDescription()}
                </p>
              </div>
            </div>
          )}

          {/* Safety Notice */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex">
              <Flag className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800">Important Information</p>
                <p className="text-sm text-blue-700">
                  Your report will be reviewed by our team. False reports may lead to account suspension. 
                  We take all reports seriously to maintain a safe marketplace.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChatDetail;